import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RevealOptions } from "reveal.js";

import type { RegisteredScreenKey } from "../../screen-registry";
import { ColorMode, DeckFeatureFlags, LogoPosition, NavigationMode } from "../../types";
import { NAVIGATION_MODE_STORAGE_KEY } from "./constants";

interface SlideState {
  prevSlideId?: string;
  currentSlideId?: RegisteredScreenKey;
  currentSlideIndex: number;
  colorMode?: ColorMode;
  logoPosition?: LogoPosition;
}

type DeckState = {
  ready: boolean;
  options: RevealOptions;
  features: DeckFeatureFlags;
} & SlideState;

const initialState: DeckState = {
  ready: false,
  currentSlideIndex: 0,
  options: {},
  features: {
    printMode: false,
  },
};

const getNavigationOptions = (navigationMode: NavigationMode, features: DeckFeatureFlags): RevealOptions => {
  if (navigationMode === "vertical" && !features.printMode && !features.isPreview) {
    return {
      embedded: false,
      view: "scroll",
      scrollProgress: true,
      margin: 0.22,
    };
  }

  return {
    embedded: true,
    view: null,
    scrollProgress: undefined,
    margin: 0.07,
  };
};

const getPreferredOptions = (features: DeckFeatureFlags): RevealOptions => {
  let navigationMode: NavigationMode = "default";

  // Use the query param if provided
  if (features.navigationMode === "vertical") {
    navigationMode = "vertical";
  } else {
    // Otherwise try pulling from local storage
    try {
      const storedMode = localStorage.getItem(NAVIGATION_MODE_STORAGE_KEY);

      if (storedMode === "vertical") {
        navigationMode = "vertical";
      }
    } catch (e) {
      // noop
    }
  }

  return getNavigationOptions(navigationMode, features);
};

const defaultOptions: RevealOptions = {
  width: 1366,
  height: 1024,
  transition: "slide",
  hash: true,
  progress: true,
  touch: true,
  controls: true,
  slideNumber: false,
  jumpToSlide: false,
  scrollActivationWidth: null,
  overview: false,
  // keyboardCondition: "focused",
};

const deckSlice = createSlice({
  name: "deck",
  initialState,
  reducers: {
    init(state, action: PayloadAction<DeckFeatureFlags>) {
      state.features = action.payload;
      state.options = {
        ...defaultOptions,
        ...getPreferredOptions(action.payload),
        controls: !action.payload.isPreview,
      };
    },
    sync(state) {
      //
    },

    // events
    ready(state, action: PayloadAction<SlideState>) {
      Object.assign(state, action.payload);
      state.ready = true;
    },
    slidechanged(state, action: PayloadAction<SlideState>) {
      Object.assign(state, action.payload);
    },

    // actions
    gotoNext(state) {
      //
    },
    gotoPrev(state) {
      //
    },
    gotoIndex(state, action: PayloadAction<number>) {
      //
    },
    syncSlideById(state, action: PayloadAction<string>) {
      //
    },
    changeColorMode(state, action: PayloadAction<ColorMode | undefined>) {
      state.colorMode = action.payload;
    },
    changeLogoPosition(state, action: PayloadAction<LogoPosition | undefined>) {
      state.logoPosition = action.payload;
    },
    changeSlideBackground(
      state,
      action: PayloadAction<{
        id: RegisteredScreenKey;
        color?: string;
        videoUrl?: string;
        imageUrl?: string;
        backgroundType?: "image" | "video";
      }>
    ) {
      //
    },
  },
});

export const {
  init: initDeck,
  sync: syncDeck,
  gotoNext,
  gotoPrev,
  gotoIndex,
  syncSlideById,
  changeColorMode,
  changeLogoPosition,
  changeSlideBackground,
} = deckSlice.actions;

export default deckSlice;
